import { useEffect } from 'react';
import { bookingUrl } from '@/lib/booking';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail } from 'lucide-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema,
} from '@/lib/seo';

export function ContactPage() {
  useEffect(() => {
    const title = 'Contact Kora — Get in touch';
    const description = 'Contact the Kora team. Email us at founder@usekora.app for sales, support, or partnership inquiries. We respond within 24 hours.';
    setDocumentMeta(title, description);
    setCanonical('/contact');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/contact', title, description),
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Kora',
        description,
        url: 'https://usekora.app/contact',
      },
    ]);

    return cleanup;
  }, []);

  return (
    <MarketingShell>
      <div className="docs-warm pt-12">

      <section className="pt-16 md:pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-zinc-900 mb-6 leading-[1.05]">
            Contact us
          </h1>
          <p className="text-lg text-zinc-500 mb-8 leading-relaxed">
            Have a question about Kora? We're here to help.
          </p>

          <div className="space-y-6 mb-12">
            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="size-10 rounded-xl bg-zinc-100 flex items-center justify-center">
                  <Mail className="size-5 text-zinc-600 " />
                </div>
                <div>
                  <div className="text-sm font-semibold text-zinc-900 ">
                    Email
                  </div>
                  <div className="text-sm text-zinc-500 ">
                    Best for all inquiries
                  </div>
                </div>
              </div>
              <a
                href="mailto:founder@usekora.app"
                className="text-sm text-zinc-900 underline"
              >
                founder@usekora.app
              </a>
              <p className="text-xs text-zinc-400 mt-2">
                We respond within 24 hours.
              </p>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="text-sm font-semibold text-zinc-900 mb-2">Book a call</div>
              <a
                href={bookingUrl('contact')}
                target="_blank"
                rel="noopener"
                className="text-sm text-zinc-900 underline"
              >
                Book a 20-minute call with the founder
              </a>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="text-sm font-semibold text-zinc-900 mb-2">
                What can we help with?
              </div>
              <ul className="space-y-2 text-sm text-zinc-600 ">
                <li>• Sales questions and plan selection</li>
                <li>• Technical support and bug reports</li>
                <li>• API integration questions</li>
                <li>• Partnership and integration inquiries</li>
                <li>• Feature requests and feedback</li>
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
            <p className="text-sm font-medium text-zinc-900 mb-4">
              Prefer to explore first?
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/docs"
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-5 py-2.5 text-sm font-medium text-zinc-900 hover:bg-zinc-50 transition-colors"
              >
                Read the docs
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
              >
                See pricing <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </MarketingShell>
  );
}
